import React from 'react';
import { Link } from 'react-router-dom';
import PageHelmet from '../../components/seo/PageHelmet';
import Layout from '../../components/ui/Layout';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { buildCanonicalUrl } from '../../constants/seo';
import { CONTACT_INFO } from '../../constants/contact';

function StepScreenshot({ title, caption, src }) {
  if (src) {
    return (
      <figure className="mb-0">
        <img
          src={src}
          alt={title}
          className="step-screenshot rounded-3 border shadow-sm w-100"
        />
        {caption && (
          <figcaption className="text-muted small text-center mt-2">{caption}</figcaption>
        )}
      </figure>
    );
  }

  return (
    <div
      className="screenshot-placeholder rounded-3 border border-2 border-dashed d-flex flex-column align-items-center justify-content-center text-center p-3"
      role="img"
      aria-label={title}
    >
      <i className="fas fa-image fa-2x text-muted mb-2" aria-hidden />
      <span className="fw-semibold text-muted small text-uppercase tracking-wide">
        Screenshot placeholder
      </span>
      <span className="text-muted small mt-1">{caption}</span>
    </div>
  );
}

const STEPS = [
  {
    number: 1,
    title: 'Create your account',
    summary: 'Sign up free with your Google account in one tap.',
    details: [
      'Tap the person icon at the top right of the screen, then choose Login.',
      'On the login page, tap “Sign in with Google”.',
      'Use the same Google account you use on your phone — no new password to remember.',
      'Add your name so the supermarket knows who the order is for.',
    ],
    screenshotSrc: '/images/how-it-works/step-1-login.png',
    placeholderCaption: 'Login page with Google sign-in',
    icon: 'fa-user-plus',
  },
  {
    number: 2,
    title: 'Choose your supermarket',
    summary: 'Pick the shop you want to buy from.',
    details: [
      'From the home page, tap See All Supermarkets or open Supermarkets from the menu.',
      'Each store card shows the name, address, and opening hours.',
      'Tap View Products on the supermarket you want to shop from.',
    ],
    screenshotSrc: '/images/how-it-works/step-2-supermarkets.png',
    placeholderCaption: 'All Supermarkets page',
    icon: 'fa-store',
  },
  {
    number: 3,
    title: 'Shop and add to your cart',
    summary: 'Find groceries and put them in your cart.',
    details: [
      'After you tap View Products, browse the store’s items — use Search or pick a category to narrow it down.',
      'Each product shows the price in Jamaican dollars and how many are in stock.',
      'Tap Add to Cart on everything you want. You can shop from more than one supermarket — each store has its own cart.',
    ],
    screenshotSrc: '/images/how-it-works/step-3-shop.png',
    placeholderCaption: 'Supermarket products with Add to Cart',
    icon: 'fa-cart-plus',
  },
  {
    number: 4,
    title: 'Check your cart',
    summary: 'Make sure everything looks right before you pay.',
    details: [
      'Tap Carts in the menu at the top to see everything you added.',
      'Check each item, change the quantity, or tap Remove if you change your mind.',
      'Look at the total at the bottom, then tap the green Checkout Cart button when you are ready.',
    ],
    screenshotSrc: '/images/how-it-works/step-4-cart.png',
    placeholderCaption: 'Your cart with Checkout Cart button',
    icon: 'fa-shopping-cart',
  },
  {
    number: 5,
    title: 'Checkout and choose how to pay',
    summary: 'Review your total and pick your payment option.',
    details: [
      'On the checkout page, choose pickup or delivery (if the store offers delivery).',
      'Check your order subtotal — with pickup, you may pay the rest when you collect your groceries.',
      'Under Payment Options, pick Pay Confirmation Fee Only or Pay Full Amount, then tap Proceed to Pay.',
    ],
    screenshotSrc: '/images/how-it-works/step-5-checkout.png',
    placeholderCaption: 'Checkout with payment options',
    icon: 'fa-truck',
  },
  {
    number: 6,
    title: 'Pay online',
    summary: 'Pay safely through our payment partner (WiPay).',
    details: [
      'After you tap Proceed to Pay, you are taken to the secure WiPay page (Powered by WiPay).',
      'Enter your card number, expiry date, and CVC, then fill in your name, phone, email, and billing address.',
      'Check the total in Jamaican dollars on the left, complete the form, and submit. Your order is sent to the supermarket once payment goes through.',
    ],
    screenshotSrc: '/images/how-it-works/step-6-payment.png',
    placeholderCaption: 'WiPay secure card payment page',
    icon: 'fa-credit-card',
  },
  {
    number: 7,
    title: 'The store packs your order',
    summary: 'Staff gather your items from the shelves.',
    details: [
      'After payment, open Carts and tap Awaiting Packing to see orders the store is working on.',
      'Your cart shows an “Awaiting Packing” badge — staff are gathering your items from the shelves.',
      'If something is out of stock, the store may contact you. When ready, the status changes to Ready for Pickup or delivery.',
    ],
    screenshotSrc: '/images/how-it-works/step-7-packing.png',
    placeholderCaption: 'Cart with Awaiting Packing status',
    icon: 'fa-box-open',
  },
  {
    number: 8,
    title: 'Pick up or receive delivery',
    summary: 'Get your groceries and enjoy!',
    details: [
      'When your order is ready, open Carts and tap Ready for Pickup — you will see a green “Ready for Pickup” badge.',
      'Go to the supermarket, collect your groceries, and pay any balance you still owe (if you only paid the confirmation fee).',
      'Tap Confirm Pickup when you have everything. For delivery, wait for your order at home and confirm when it arrives.',
    ],
    screenshotSrc: '/images/how-it-works/step-8-pickup.png',
    placeholderCaption: 'Ready for Pickup with Confirm Pickup button',
    icon: 'fa-check-circle',
  },
];

export default function HowItWorks() {
  const seoTitle = 'How It Works | Pack My Cart';
  const seoDescription =
    'Learn how to shop groceries online with Pack My Cart — from sign up and browsing supermarkets to payment, packing, and pickup or delivery in Jamaica.';
  const pageUrl = buildCanonicalUrl('/how-it-works');

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to shop groceries online with Pack My Cart',
    description: seoDescription,
    url: pageUrl,
    step: STEPS.map((step) => ({
      '@type': 'HowToStep',
      position: step.number,
      name: step.title,
      text: [step.summary, ...step.details].join(' '),
      url: `${pageUrl}#step-${step.number}`,
    })),
  };

  return (
    <Layout>
      <PageHelmet
        title={seoTitle}
        description={seoDescription}
        path="/how-it-works"
        jsonLd={howToSchema}
      />

      <style>{`
        .how-it-works-hero {
          background: linear-gradient(135deg, #f8f9fa 0%, #e8f5e9 100%);
          border-radius: 12px;
        }
        .step-screenshot {
          display: block;
          height: auto;
        }
        .screenshot-placeholder {
          min-height: 200px;
          background: #f8f9fa;
          border-color: #ced4da !important;
        }
        @media (min-width: 768px) {
          .screenshot-placeholder {
            min-height: 240px;
          }
        }
        .step-number-badge {
          width: 2.5rem;
          height: 2.5rem;
          font-size: 1.1rem;
        }
        .how-step-card {
          border-left: 4px solid var(--text-dark-green, #336555);
        }
      `}</style>

      <Container className="mt-4 mb-5">
        <div className="how-it-works-hero text-center px-3 py-4 py-md-5 mb-4 mb-md-5">
          <h1 className="text-dark-green fw-bold mb-3">How Pack My Cart Works</h1>
          <p className="lead mb-0 mx-auto" style={{ maxWidth: '42rem' }}>
            Shop from your favourite Jamaican supermarkets on your phone or computer.
            Here is the full journey — from signing up to getting your groceries — explained in plain language.
          </p>
        </div>

        <Row className="justify-content-center mb-4 mb-md-5">
          <Col md={10} lg={8}>
            <Card className="border-0 shadow-sm bg-white">
              <Card.Body className="p-4">
                <h2 className="h5 text-dark-green mb-3">
                  <i className="fas fa-route me-2" aria-hidden />
                  Quick overview
                </h2>
                <p className="mb-2 text-muted">
                  Sign up → Pick a supermarket → Add items to cart → Checkout → Pay → Store packs → Pick up or delivery
                </p>
                <p className="mb-0 small text-muted">
                  Most orders take a short time to pack. You will see updates on your Order Status page and can turn on notifications when you log in.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {STEPS.map((step, index) => {
          const imageOnRight = index % 2 === 1;
          return (
            <Row
              key={step.number}
              className={`mb-5 pb-4 align-items-center g-4 ${index < STEPS.length - 1 ? 'border-bottom' : ''}`}
            >
              <Col
                md={6}
                className={imageOnRight ? 'order-md-2' : 'order-md-1'}
              >
                <div className="d-flex align-items-start gap-3 mb-3">
                  <Badge
                    bg="success"
                    className="step-number-badge rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                  >
                    {step.number}
                  </Badge>
                  <div>
                    <h2 className="h4 text-dark-green mb-1">
                      <i className={`fas ${step.icon} me-2`} aria-hidden />
                      {step.title}
                    </h2>
                    <p className="text-muted mb-0 fw-medium">{step.summary}</p>
                  </div>
                </div>
                <Card className="how-step-card border-0 shadow-sm">
                  <Card.Body>
                    <ul className="mb-0 ps-3">
                      {step.details.map((line) => (
                        <li key={line} className="mb-2">
                          {line}
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
              <Col
                md={6}
                className={imageOnRight ? 'order-md-1' : 'order-md-2'}
              >
                <StepScreenshot
                  title={`Step ${step.number}: ${step.title}`}
                  caption={step.placeholderCaption}
                  src={step.screenshotSrc}
                />
              </Col>
            </Row>
          );
        })}

        <Row className="justify-content-center mt-2">
          <Col md={10} lg={8}>
            <Card className="border-0 shadow-sm bg-light">
              <Card.Body className="text-center p-4 p-md-5">
                <h2 className="h4 text-dark-green mb-3">Ready to start shopping?</h2>
                <p className="text-muted mb-4">
                  It only takes a minute to sign in. Browse supermarkets near you and fill your cart today.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
                  <Button as={Link} to="/login" variant="success" size="lg">
                    <i className="fas fa-sign-in-alt me-2" aria-hidden />
                    Sign in to shop
                  </Button>
                  <Button as={Link} to="/supermarkets" variant="outline-primary" size="lg">
                    <i className="fas fa-store me-2" aria-hidden />
                    View supermarkets
                  </Button>
                </div>
              </Card.Body>
            </Card>

            <p className="text-center text-muted small mt-4 mb-0">
              Questions? Email us at{' '}
              <a href={`mailto:${CONTACT_INFO.email}`}>{CONTACT_INFO.email}</a>
              {' '}or read our{' '}
              <Link to="/refund-policy">refund policy</Link>.
            </p>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
}
